
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import KTFolder from "@/components/files/Folder.vue";
import KTFile from "@/components/files/File.vue";
import {
  listKnowledgeBases,
  getKnowledgeBaseStorage,
  createKnowledgeBase,
  updateKnowledgeBase,
  deleteKnowledgeBase,
  listKnowledgeBaseItems,
  createKnowledgeBaseFolder,
  addKnowledgeBaseLink,
  renameKnowledgeBaseItem,
  deleteKnowledgeBaseItem,
  uploadKnowledgeBaseFile,
  KB_FILE_ACCEPT,
  formatStorageBytes,
  fileIconType
} from "@/api/knowledgeBases.api.js";

export default defineComponent({
  name: "knowledge-bases",
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    KTFolder,
    KTFile
  },
  data() {
    return {
      loading: true,
      loadError: "",
      knowledgeBases: [] as any[],
      canShare: false,
      storageUsedBytes: 0,
      storageLimitBytes: 1073741824,
      selectedKb: null as any,
      items: [] as any[],
      canManageItems: false,
      currentParentId: 0,
      breadcrumbs: [] as { id: number; name: string }[],
      showCreateModal: false,
      showNewFolderModal: false,
      showAddLinkModal: false,
      newKbName: "",
      newKbLabel: "",
      newFolderName: "",
      linkName: "",
      linkUrl: "",
      KB_FILE_ACCEPT,
      indexPollTimer: null as ReturnType<typeof setInterval> | null
    };
  },
  computed: {
    groupedKnowledgeBases(): { label: string; items: any[] }[] {
      const groups = new Map<string, any[]>();
      for (const kb of this.knowledgeBases) {
        const label = kb.label || "General";
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label)!.push(kb);
      }
      return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
    },
    folderItems(): any[] {
      return this.items.filter((item) => item.itemType === "folder");
    },
    fileItems(): any[] {
      return this.items.filter((item) => item.itemType !== "folder");
    }
  },
  async mounted() {
    await this.loadList();
    this.loading = false;
  },
  beforeUnmount() {
    this.stopIndexPolling();
  },
  methods: {
    formatStorageBytes,
    fileIconType,
    async loadList() {
      this.loadError = "";
      try {
        const [listResponse, storageResponse] = await Promise.all([
          listKnowledgeBases(),
          getKnowledgeBaseStorage()
        ]);
        this.knowledgeBases = listResponse.data?.knowledgeBases || [];
        this.canShare = Boolean(listResponse.data?.canShare);
        this.storageUsedBytes = Number(storageResponse.data?.usedBytes || 0);
        this.storageLimitBytes = Number(storageResponse.data?.limitBytes || this.storageLimitBytes);
      } catch (err) {
        this.loadError = (err as any)?.response?.data?.message || "Unable to load knowledge bases.";
      }
    },
    async openKnowledgeBase(kb: any) {
      this.selectedKb = kb;
      this.currentParentId = 0;
      this.breadcrumbs = [];
      this.loading = true;
      await this.loadItems();
      this.loading = false;
    },
    closeKnowledgeBase() {
      this.stopIndexPolling();
      this.selectedKb = null;
      this.items = [];
      this.loadList();
    },
    async loadItems() {
      if (!this.selectedKb) return;
      const response = await listKnowledgeBaseItems(this.selectedKb.id, this.currentParentId);
      this.items = response.data?.items || [];
      this.canManageItems = Boolean(response.data?.canManage);
      this.storageUsedBytes = Number(response.data?.usedBytes || this.storageUsedBytes);
      this.storageLimitBytes = Number(response.data?.limitBytes || this.storageLimitBytes);
      this.syncIndexPolling();
    },
    hasPendingIndexing(): boolean {
      return this.fileItems.some((item) => {
        const status = String(item.indexingStatus || "").toLowerCase();
        return status === "pending" || status === "processing";
      });
    },
    syncIndexPolling() {
      if (this.hasPendingIndexing()) {
        if (!this.indexPollTimer) {
          this.indexPollTimer = setInterval(() => {
            this.loadItems().catch(() => undefined);
          }, 5000);
        }
        return;
      }
      this.stopIndexPolling();
    },
    stopIndexPolling() {
      if (this.indexPollTimer) {
        clearInterval(this.indexPollTimer);
        this.indexPollTimer = null;
      }
    },
    async openFolder(folderId: number, folderName?: string) {
      if (folderId > 0) {
        const existingIndex = this.breadcrumbs.findIndex((b) => b.id === folderId);
        if (existingIndex >= 0) {
          this.breadcrumbs = this.breadcrumbs.slice(0, existingIndex + 1);
        } else if (folderName) {
          this.breadcrumbs.push({ id: folderId, name: folderName });
        }
      } else {
        this.breadcrumbs = [];
      }
      this.currentParentId = folderId;
      await this.loadItems();
    },
    openItem(item: any) {
      if (!item.url) return;
      window.open(item.url, "_blank");
    },
    async createKb() {
      if (!this.newKbName.trim()) {
        Swal.fire({ icon: "error", title: "Name is required" });
        return;
      }
      try {
        await createKnowledgeBase({
          name: this.newKbName.trim(),
          label: this.newKbLabel.trim() || null
        });
        this.showCreateModal = false;
        this.newKbName = "";
        this.newKbLabel = "";
        await this.loadList();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not create knowledge base",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async promptEditKb(kb: any) {
      const result = await Swal.fire({
        title: "Edit Knowledge Base",
        html:
          '<input id="kb-name" class="swal2-input" placeholder="Name">' +
          '<input id="kb-label" class="swal2-input" placeholder="Label (optional)">',
        focusConfirm: false,
        showCancelButton: true,
        didOpen: () => {
          const nameInput = document.getElementById("kb-name") as HTMLInputElement;
          const labelInput = document.getElementById("kb-label") as HTMLInputElement;
          if (nameInput) nameInput.value = kb.name;
          if (labelInput) labelInput.value = kb.label || "";
        },
        preConfirm: () => {
          const nameInput = document.getElementById("kb-name") as HTMLInputElement;
          const labelInput = document.getElementById("kb-label") as HTMLInputElement;
          return {
            name: nameInput?.value?.trim(),
            label: labelInput?.value?.trim() || null
          };
        }
      });
      if (!result.isConfirmed || !result.value?.name) return;

      try {
        await updateKnowledgeBase(kb.id, result.value);
        await this.loadList();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update knowledge base",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async toggleShare(kb: any) {
      try {
        await updateKnowledgeBase(kb.id, { sharedToDownline: !kb.sharedToDownline });
        await this.loadList();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update sharing",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async confirmDeleteKb(kb: any) {
      const result = await Swal.fire({
        title: "Delete knowledge base?",
        text: `Delete "${kb.name}" and all of its items?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonClass: "btn btn-danger"
      });
      if (!result.isConfirmed) return;

      try {
        await deleteKnowledgeBase(kb.id);
        await this.loadList();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not delete knowledge base",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async createFolder() {
      if (!this.selectedKb || !this.newFolderName.trim()) return;
      try {
        await createKnowledgeBaseFolder(this.selectedKb.id, this.currentParentId, this.newFolderName.trim());
        this.showNewFolderModal = false;
        this.newFolderName = "";
        await this.loadItems();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not create folder",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async addLink() {
      if (!this.selectedKb || !this.linkName.trim() || !this.linkUrl.trim()) return;
      try {
        await addKnowledgeBaseLink(
          this.selectedKb.id,
          this.currentParentId,
          this.linkName.trim(),
          this.linkUrl.trim()
        );
        this.showAddLinkModal = false;
        this.linkName = "";
        this.linkUrl = "";
        await this.loadItems();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not add link",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async handleFileUpload(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file || !this.selectedKb) return;

      try {
        const response = await uploadKnowledgeBaseFile(this.selectedKb.id, this.currentParentId, file);
        this.storageUsedBytes = Number(response.data?.usedBytes || this.storageUsedBytes);
        await this.loadItems();
        Swal.fire({ icon: "success", title: "File uploaded", timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Upload failed",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async promptRename(item: any) {
      const result = await Swal.fire({
        title: "Rename",
        input: "text",
        inputValue: item.name,
        showCancelButton: true
      });
      if (!result.isConfirmed || !result.value || !this.selectedKb) return;

      try {
        await renameKnowledgeBaseItem(this.selectedKb.id, item.id, String(result.value).trim());
        await this.loadItems();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not rename item",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    async confirmDeleteItem(item: any) {
      const result = await Swal.fire({
        title: "Delete item?",
        text: `Delete "${item.name}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete"
      });
      if (!result.isConfirmed || !this.selectedKb) return;

      try {
        const response = await deleteKnowledgeBaseItem(this.selectedKb.id, item.id);
        this.storageUsedBytes = Number(response.data?.usedBytes || this.storageUsedBytes);
        await this.loadItems();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not delete item",
          text: (err as any)?.response?.data?.message || "Please try again."
        });
      }
    },
    formatDate(value: any) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString();
    }
  }
});
